import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from "react";

import { resolveColumnEdit, type DataTableColumn } from "@/components/table/columns";
import { cx } from "@/lib/cx";
import type { CellEditState } from "@/lib/inline-edit";

export interface InlineEditCellProps<Row extends { id: string }> {
  /** The whole row this cell belongs to — needed to build the synthetic row
   * `column.render` expects when rendering an *unsaved* value through the
   * same label map a resting cell already uses (review finding: `role`'s
   * raw enum value, e.g. `read_only`, was leaking into the UI in every
   * state but resting). */
  row: Row;
  column: DataTableColumn<Row>;
  /** The resting-state rendered content — identical to a read-only cell
   * (VC-2.3-01): whatever `DataTable`'s own `renderCell` would have shown. */
  children: ReactNode;
  /** The real underlying field value (`row[column.key]`, not its rendered
   * label) — what the editor is pre-filled with and what an unmodified
   * commit would resend. */
  rawValue: string;
  isActive: boolean;
  onActivate: () => void;
  cellState: CellEditState | undefined;
  onCommit: (rawValue: string) => void;
  /**
   * Story 15.5: fired from this component's own unmount cleanup, in place
   * of `onCommit` above, when the field is active with a `draft` that
   * differs from `rawValue` and the component is being torn down — most
   * commonly a route navigation away from this page while the field was
   * still open and never blurred. React fires no synthetic blur on
   * unmount, so without this the typed value would simply vanish (the
   * one real bug this story fixes). The caller (`RecordPage`/`DataTable`)
   * routes this through the identical commit path `onCommit` already
   * uses, distinguished only by how it reports success/failure back —
   * this cell is gone by the time that resolves, so there is nothing left
   * here to show a resting/error state on.
   */
  onCommitOnLeave: (rawValue: string) => void;
  onCancel: () => void;
  onTab: (rawValue: string) => void;
  /** `Shift+Tab` — the previous editable cell (review finding 2: this used
   * to not exist at all, so `Tab`'s own forward handler ran regardless of
   * `event.shiftKey`). */
  onShiftTab: (rawValue: string) => void;
  onRetry: () => void;
  /** Whether `Tab`/`Shift+Tab` has somewhere to go. When the direction has
   * no target, this component does not intercept the key at all — native
   * `Tab` handles focus movement, and the field's own `onBlur` still
   * commits (review finding 2: `Tab` at the last cell of the last row used
   * to `preventDefault()` unconditionally and then never move focus
   * anywhere, dropping it to `<body>`). */
  hasNextEditableCell: boolean;
  hasPreviousEditableCell: boolean;
}

/**
 * One editable table cell (R-W3, `DESIGN.md`'s `inline-edit-cell`): resting
 * (indistinguishable from read-only, VC-2.3-01), editing (accent-bordered
 * field sized to the cell, VC-2.3-02), and unsaved (accent border plus a
 * trailing warning dot, kept until the write resolves, VC-2.3-04) — the
 * conflict *banner* itself is not rendered here (judgement call 4: it sits
 * beside the row, which is `DataTable`'s job as the thing that owns the
 * row); this component only keeps the typed value and the warning marker
 * visible while a conflict is outstanding.
 *
 * A column with no `edit` config renders exactly `children` and nothing
 * else — this component is a no-op wrapper for every read-only cell, which
 * is what keeps `DataTable` itself free of an `if (editable) {...} else
 * {...}` branch at every call site.
 */
export function InlineEditCell<Row extends { id: string }>({
  row,
  column,
  children,
  rawValue,
  isActive,
  onActivate,
  cellState,
  onCommit,
  onCommitOnLeave,
  onCancel,
  onTab,
  onShiftTab,
  onRetry,
  hasNextEditableCell,
  hasPreviousEditableCell,
}: InlineEditCellProps<Row>) {
  // Story 15.16: `column.edit` may now be a function of the row (per-row
  // editability, `columns.ts`'s own doc comment) — resolved here, once, so
  // every use of `edit` below (kind checks, `.options`) reads a concrete
  // config exactly as it always did.
  const edit = resolveColumnEdit(column, row);
  const [draft, setDraft] = useState(rawValue);
  const fieldRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    if (isActive) {
      setDraft(cellState?.value ?? rawValue);
      committedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      fieldRef.current?.focus();
      if (fieldRef.current instanceof HTMLInputElement) {
        fieldRef.current.select();
      }
    }
  }, [isActive]);

  // Story 15.5 (auto-save on navigate-away): plain mutable refs, kept in
  // step with every render, so the unmount cleanup below — captured once,
  // on mount, per React's own `useEffect(() => cleanup, [])` contract —
  // reads whatever was actually current the instant this cell was torn
  // down, not whatever was current when the cleanup closure was first
  // created.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const rawValueRef = useRef(rawValue);
  rawValueRef.current = rawValue;
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const onCommitOnLeaveRef = useRef(onCommitOnLeave);
  onCommitOnLeaveRef.current = onCommitOnLeave;

  useEffect(() => {
    return () => {
      // Only the field that was actually open (`isActive`) and genuinely
      // dirty (`draft` differs from the last value this cell was told is
      // committed) auto-saves — a resting field unmounting (every other
      // field on the page, on every ordinary navigation) must never fire a
      // phantom write (VC-15.5-02). `committedRef` is the same guard
      // `commitOnce` below already uses for blur/Enter/Tab — if one of
      // those already committed this exact draft moments before unmount,
      // it has already flipped this to `true`, and this cleanup is a
      // no-op, closing the double-commit race VC-15.5-06 checks for.
      if (isActiveRef.current && !committedRef.current && draftRef.current !== rawValueRef.current) {
        committedRef.current = true;
        onCommitOnLeaveRef.current(draftRef.current);
      }
    };
    // Deliberately empty deps: this must run its cleanup exactly once, on
    // this cell's own unmount — not on every prop change while mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Considered and deferred (Story 15.5, judgement call 3): a
  // `window.beforeunload` handler for the tab-close/refresh case, which
  // this `useEffect` cleanup cannot reach (the page is gone before an
  // unmount would ever fire). The AC only asks for in-app route
  // navigation. A real fix for the tab-close case would need either a
  // native "leave site?" confirm dialog — the confirmation-dialog model
  // this story's own Intent explicitly decided against for the in-app
  // case, and one this app has no existing precedent for — or a
  // fire-and-forget `fetch(..., { keepalive: true })` whose outcome can
  // never be observed (the page is unloading), so no toast could honestly
  // report success or failure either way. Left out rather than shipped
  // half-honest.

  if (edit === undefined) {
    return <>{children}</>;
  }

  const unsaved = cellState !== undefined;

  /**
   * The plain-language rendering of an arbitrary raw value for this column
   * — the same `column.render` a resting cell already uses, applied to a
   * synthetic row carrying the value in question, so `role`'s unsaved,
   * erroring or conflicted state shows "Read-only", never `read_only`
   * (review finding). Text columns have no `render`, so this is the
   * identity function for them, exactly as it always was.
   */
  function renderedValue(value: string): ReactNode {
    if (column.render) {
      return column.render({ ...row, [column.key]: value } as Row);
    }
    return value;
  }

  function commitOnce(value: string) {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;
    onCommit(value);
  }

  function restoreFocusToCell(event: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
    // The row itself is about to lose its `<input>`/`<select>` (reverting
    // to resting) — without this, focus falls through to `<body>` and a
    // keyboard user loses their place on the page entirely (review
    // finding). `<td>` isn't natively focusable; giving it `tabIndex=-1`
    // makes `.focus()` work without adding it to the normal Tab order.
    //
    // Widened (Story 2.5), not duplicated: this same component is now also
    // used at row granularity by the record page's `FieldRow`, which is not
    // a `<td>` — it marks its own wrapper `data-inline-edit-boundary` for
    // exactly this lookup, so the identical mechanism keeps working outside
    // a `<table>` without a second focus-restore implementation.
    const cell = event.currentTarget.closest("td, [data-inline-edit-boundary]");
    if (cell instanceof HTMLElement) {
      cell.tabIndex = -1;
      cell.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      committedRef.current = true; // a blur Esc triggers must not also commit
      onCancel();
      restoreFocusToCell(event);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commitOnce(draft);
      return;
    }
    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (!hasPreviousEditableCell) {
          // Nothing to jump to — let native Shift+Tab move focus wherever
          // it naturally would; the field's own onBlur still commits.
          return;
        }
        event.preventDefault();
        committedRef.current = true; // the blur Tab triggers must not double-commit
        onShiftTab(draft);
        return;
      }
      if (!hasNextEditableCell) {
        return;
      }
      event.preventDefault();
      committedRef.current = true;
      onTab(draft);
    }
  }

  // The row itself navigates on click (`DataTable`'s own `<tr onClick>`) —
  // any click landing inside this cell, active or resting, must never bubble
  // there: activating an edit, moving the caret inside the field, or
  // pressing Retry must not also open the record page underneath it.
  const stopBubble = { onClick: (event: { stopPropagation: () => void }) => event.stopPropagation() };

  if (isActive) {
    const fieldClasses =
      "h-tap-target-min w-full rounded-sm border border-border-focus bg-surface px-2 text-body text-ink";
    if (edit.kind === "select" || edit.kind === "transition") {
      return (
        <select
          ref={fieldRef as Ref<HTMLSelectElement>}
          className={fieldClasses}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => commitOnce(draft)}
          {...stopBubble}
        >
          {edit.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={fieldRef as Ref<HTMLInputElement>}
        type="text"
        className={fieldClasses}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commitOnce(draft)}
        {...stopBubble}
      />
    );
  }

  return (
    <div
      className={cx(unsaved ? "-mx-2 -my-1 rounded-sm border border-accent px-2 py-1" : undefined)}
      {...stopBubble}
    >
      <button
        type="button"
        onClick={onActivate}
        className="block w-full cursor-default border-0 bg-transparent p-0 text-left font-[inherit] text-[inherit]"
      >
        <span className="flex w-full items-center justify-between gap-2">
          <span>{unsaved ? renderedValue(cellState.value) : children}</span>
          {unsaved ? (
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-warning" />
          ) : null}
        </span>
      </button>
      {cellState?.status === "error" ? (
        <p className="mt-1 text-meta text-danger">
          {cellState.message ?? "Couldn't save."} Your value is still here.{" "}
          <button type="button" className="underline" onClick={onRetry}>
            Retry
          </button>
        </p>
      ) : null}
    </div>
  );
}
